import {
    VbBidRowStatus,
    VbBidTableRow,
    fmtDate,
    fmtDateTime,
    formatCompactCurrency,
} from '@viewbid/ui';
import { UserBidHistory } from '../../modules/profiles/model/userBidHistory';
import { isTimeAhead } from './lot-countdown.util';

/**
 * Bid-history → vb-bid-table seam (profile "My bidding" redesign).
 *
 * Pure mappers from the store's UserBidHistory wire rows to the library's
 * VbBidTableRow shape. The caller owns the countdown tick: it passes the
 * per-lot seconds (LotCountdownTracker.secondsLeft) and re-maps every second
 * so the OnPush table sees a fresh array reference.
 */

// The API and socket payloads sometimes return numeric money fields as
// strings (currentBid is string on the wire). Coerce here so the table gets
// numbers, not string concatenation.
function asNumber(value: unknown): number | undefined {
    if (value === null || value === undefined || value === '') return undefined;
    const n: number = typeof value === 'number' ? value : Number(value);
    return Number.isFinite(n) ? n : undefined;
}

/**
 * Open/closed check for a bid-history row — same rule the legacy
 * UserBidsComponent.isLotOpen used: open while the lot's end datetime is
 * still in the future.
 */
export function isBidLotOpen(
    lotToDatetime: string | undefined,
    nowMs: number,
): boolean {
    return isTimeAhead(lotToDatetime, nowMs);
}

/** Fallback tick source when the caller has no tracker entry for the lot. */
function secondsUntil(
    lotToDatetime: string | undefined,
    nowMs: number,
): number {
    if (!lotToDatetime) return 0;
    return Math.max(
        0,
        Math.floor((new Date(lotToDatetime).getTime() - nowMs) / 1000),
    );
}

/** Reserve satisfied per the winning rule: no reserve set, or the final
 *  price reached it. (reserveAmount absent/0 = no reserve.) */
function isReserveMet(item: UserBidHistory): boolean {
    const reserve: number = asNumber(item.reserveAmount) ?? 0;
    if (reserve <= 0) return true;
    return (asNumber(item.currentBid) ?? 0) >= reserve;
}

/**
 * Row lifecycle status. The wire has no separate won/lost flag, so closed
 * rows derive it: below reserve the high bid bought nothing — nobody won or
 * lost, the row reads 'reserve-not-met' for every bidder. With the reserve
 * satisfied, won/lost falls out of the final high-bidder check.
 */
function bidRowStatus(
    open: boolean,
    isHighBidder: boolean,
    reserveMet: boolean,
): VbBidRowStatus {
    if (open) return isHighBidder ? 'winning' : 'losing';
    if (!reserveMet) return 'reserve-not-met';
    return isHighBidder ? 'won' : 'lost';
}

/**
 * Open rows' quick-bid CTA — the same "Bid · $X" lockup the lot card and
 * lot list rail use (amount = the lot's minimum next bid). Falls back to the
 * table's default label when the wire hasn't provided a next-bid amount.
 */
function quickBidLabel(minimumBidAmount?: number): string | undefined {
    const amount: number | undefined = asNumber(minimumBidAmount);
    if (!amount || amount <= 0) return undefined;
    return `Bid · ${formatCompactCurrency(amount)}`;
}

/** "2026-07-09" close date for closed rows — the table renders it after
 *  its own muted "Closed" prefix. */
function closedLabel(lotToDatetime?: string): string | undefined {
    return fmtDate(lotToDatetime) || undefined;
}

/** "2026-09-02 4:15 PM" stamp of the viewer's bid — the table's 'recent'
 *  variant renders it after its own muted "Placed" prefix. */
function placedLabel(userBidCreatedOn?: string | null): string | undefined {
    return fmtDateTime(userBidCreatedOn) || undefined;
}

/**
 * Maps one bid-history wire row to a vb-bid-table row.
 *
 * - status: open → winning/losing from currentHighBidderId === currentUserId;
 *   closed → 'reserve-not-met' when the final price stayed below the reserve
 *   (nobody won or lost), else won/lost from that same high-bidder check (no
 *   explicit outcome flag exists on the wire).
 * - proxy: the wire's explicit isProxy flag (also set by the optimistic
 *   post-bid patch), which renders the circled "P" badge.
 * - secondsLeft only carries for open rows; closed rows get the
 *   YYYY-MM-DD close date (the table adds its "Closed" prefix).
 * - placedLabel is the viewer's bid timestamp — only the 'recent' variant
 *   (account overview) renders it; the ledger ignores it.
 * - imageUrl stays undefined when the row has none so the table renders the
 *   design system's warm stripe placeholder (never the gray fallback art).
 */
export function userBidHistoryToBidTableRow(
    item: UserBidHistory,
    currentUserId: string,
    nowMs: number,
    secondsLeft?: number,
): VbBidTableRow {
    const open: boolean = isBidLotOpen(item.lotToDatetime, nowMs);
    const isHighBidder: boolean =
        !!currentUserId && item.currentHighBidderId === currentUserId;
    const status: VbBidRowStatus = bidRowStatus(
        open,
        isHighBidder,
        isReserveMet(item),
    );
    return {
        id: item.auctionLotId ?? item.lotId ?? '',
        lotCode: item.lotNumber ? `LOT ${item.lotNumber}` : '',
        title: item.title ?? '',
        location: item.location?.name ?? item.pickupLocation ?? '',
        imageUrl: item.imageURL || undefined,
        open,
        secondsLeft: open
            ? (secondsLeft ?? secondsUntil(item.lotToDatetime, nowMs))
            : undefined,
        closedLabel: open ? undefined : closedLabel(item.lotToDatetime),
        placedLabel: placedLabel(item.userBidCreatedOn),
        maxBid: asNumber(item.userBid) ?? 0,
        currentBid: asNumber(item.currentBid) ?? 0,
        proxy: !!item.isProxy,
        status,
        actionLabel: open ? quickBidLabel(item.minimumBidAmount) : undefined,
        // Same rule as the lot card's quick-bid CTA: you can't bid against
        // yourself while you're already the high bidder.
        actionDisabled: open && isHighBidder,
        // Closed lots are only navigable by their winner (high bidder +
        // reserve met) — everyone else's rows go inert: no row click, no
        // View-lot action, no lot page to land on.
        navigable: open || status === 'won',
    };
}

/**
 * Page-level mapper: pairs each row with its tracker seconds (keyed by
 * auctionLotId, the shape LotCountdownTracker.secondsLeft exposes) and
 * returns a fresh array so the OnPush table re-renders.
 */
export function userBidHistoryToBidTableRows(
    items: UserBidHistory[],
    currentUserId: string,
    nowMs: number,
    secondsLeftByLotId: Record<string, number> = {},
): VbBidTableRow[] {
    return items.map((item: UserBidHistory) =>
        userBidHistoryToBidTableRow(
            item,
            currentUserId,
            nowMs,
            item.auctionLotId
                ? secondsLeftByLotId[item.auctionLotId]
                : undefined,
        ),
    );
}
